import React from "react";

function PrivacyPolicy() {
  const sections = [
    {
      title: "1. Overview",
      body: [
        `This Privacy Policy explains how CarFido ("CarFido," "Company," "we," "us") collects, uses, shares, and protects personal data. We comply with applicable global data protection laws, including GDPR, CCPA/CPRA, and other relevant regulations.`,
      ],
    },
    {
      title: "2. Categories of Data We Collect",
      body: [
        "User Data: contact information such as phone number or email.",
        "Vehicle Data: make, model, mileage, modifications.",
        "Conversation Data: messages and interactions with CarFido.",
        "Generated Data: diagnostic outputs, service logs, insights.",
        "Usage Data: interactions, analytics, performance data.",
        "Integration Data: data from connected services, such as OBD or APIs, if applicable.",
      ],
    },
    {
      title: "3. How We Collect Data",
      body: [
        "Directly from you when you interact with CarFido.",
        "Automatically through usage of the Platform.",
        "From third-party services when integrations are enabled.",
      ],
    },
    {
      title: "4. How We Use Data",
      body: [
        "We may review, analyze, and process user interactions and conversations with CarFido to improve the accuracy, safety, and performance of the Platform. This may include the use of internal tools and authorized personnel to evaluate system outputs, identify errors, and enhance user experience. Such analysis is conducted for internal purposes only and is subject to appropriate safeguards. We do not publicly disclose user conversations and do not sell personal data.",
        "We use collected data to operate and maintain the Platform, improve functionality and AI performance, personalize your experience, develop new features, ensure security, prevent fraud, comply with legal obligations, and communicate service updates or respond to support inquiries.",
      ],
    },
    {
      title: "5. Model Training & AI Use",
      body: [
        "We do not use your personal data, vehicle-specific data, or conversation data to train or fine-tune general-purpose AI models without your explicit, affirmative consent, also known as opt-in consent.",
        "We may, however, use user interactions and generated outputs to improve the performance, accuracy, and safety of the Platform, including through internal evaluation, system tuning, prompt refinement, and development of product features. Where feasible, such improvements may be performed using anonymized or aggregated data.",
      ],
    },
    {
      title: "6. How We Share Data",
      body: [
        "We do not sell personal data.",
        "We may share data with service providers, including hosting, analytics, and communications providers.",
        "We may share data with third-party integrations you enable.",
        "We may share data with legal authorities when required.",
        "We may share data in connection with business transfers, such as mergers or acquisitions.",
      ],
    },
    {
      title: "7. Third-Party AI Platforms",
      body: [
        "If you interact with CarFido through third-party AI platforms or services, those platforms may process your data under their own policies. This Policy does not govern third-party practices.",
      ],
    },
    {
      title: "8. User Rights",
      body: [
        "Depending on your jurisdiction, you may have rights to access your data.",
        "Depending on your jurisdiction, you may have rights to correct inaccurate data.",
        "Depending on your jurisdiction, you may have rights to delete your data.",
        "Depending on your jurisdiction, you may have rights to restrict or object to processing.",
        "Depending on your jurisdiction, you may have rights to request data portability.",
        "To exercise these rights, contact support@carfido.com.",
      ],
    },
    {
      title: "9. Data Retention",
      body: [
        "We retain personal data only as long as necessary to provide services, comply with legal obligations, and maintain security.",
      ],
    },
    {
      title: "10. Security",
      body: [
        "We implement reasonable administrative, technical, and physical safeguards to protect data, though no system is completely secure.",
      ],
    },
    {
      title: "11. International Data Transfers",
      body: [
        "Data may be processed in the United States or other jurisdictions where our service providers operate.",
      ],
    },
    {
      title: "12. Children’s Privacy",
      body: [
        "We do not knowingly collect data from children under 13. If identified, such data will be deleted.",
      ],
    },
    {
      title: "13. Cookies & Tracking",
      body: [
        "If accessed via web, we may use cookies and similar technologies to improve functionality and analyze usage.",
      ],
    },
    {
      title: "14. Changes to Policy",
      body: [
        "We may update this Privacy Policy from time to time. Continued use constitutes acceptance of the updated Policy.",
      ],
    },
    {
      title: "15. Contact",
      body: [
        "For questions or requests, contact support@carfido.com.",
      ],
    },
  ];

  const styles = {
    page: {
      minHeight: "100vh",
      margin: 0,
      background: "#F9F9F9",
      color: "#414141",
      fontFamily: "Nunito, Inter, sans-serif",
      lineHeight: 1.7,
    },
    hero: {
      background: "linear-gradient(160deg, #1F1F1F 0%, #2A2A2A 60%, #414141 100%)",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      color: "#ffffff",
    },
    heroInner: {
      maxWidth: "1120px",
      margin: "0 auto",
      padding: "80px 24px 60px",
    },
    eyebrow: {
      margin: "0 0 12px",
      color: "#D81F26",
      fontSize: "12px",
      fontWeight: 800,
      letterSpacing: "0.25em",
      textTransform: "uppercase",
    },
    h1: {
      margin: "0 0 16px",
      color: "#ffffff",
      fontSize: "44px",
      lineHeight: 1.15,
      fontWeight: 900,
      maxWidth: "820px",
      letterSpacing: "-0.02em",
    },
    intro: {
      maxWidth: "760px",
      margin: 0,
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "17px",
      fontWeight: 500,
    },
    meta: {
      marginTop: "20px",
      color: "rgba(255, 255, 255, 0.5)",
      fontSize: "13px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    container: {
      maxWidth: "1120px",
      margin: "0 auto",
      padding: "48px 24px 80px",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 300px",
      gap: "36px",
      alignItems: "start",
    },
    main: {
      maxWidth: "780px",
      minWidth: 0,
    },
    section: {
      padding: "30px 32px",
      marginBottom: "24px",
      border: "1px solid #E6E6E6",
      borderRadius: "24px",
      background: "rgba(255, 255, 255, 0.6)",
      backdropFilter: "blur(8px)",
      boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
    },
    h2: {
      margin: "0 0 16px",
      color: "#414141",
      fontSize: "20px",
      lineHeight: 1.3,
      fontWeight: 800,
      letterSpacing: "-0.01em",
    },
    p: {
      margin: 0,
      color: "#414141",
      fontSize: "15px",
      lineHeight: 1.75,
      fontWeight: 500,
    },
    ul: {
      margin: 0,
      paddingLeft: "20px",
      color: "#414141",
      fontSize: "15px",
      lineHeight: 1.75,
      fontWeight: 500,
    },
    li: {
      marginBottom: "10px",
    },
    aside: {
      position: "sticky",
      top: "100px",
    },
    sideCard: {
      border: "1px solid #E6E6E6",
      borderRadius: "24px",
      background: "#ffffff",
      padding: "24px",
      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
    },
    sideTitle: {
      margin: "0 0 12px",
      color: "#414141",
      fontSize: "16px",
      fontWeight: 800,
      letterSpacing: "-0.01em",
    },
    sideText: {
      margin: "0 0 16px",
      color: "#414141",
      fontSize: "13.5px",
      lineHeight: 1.6,
      fontWeight: 500,
    },
    contactButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: "12px 20px",
      borderRadius: "9999px",
      background: "#000000",
      color: "#ffffff",
      textDecoration: "none",
      fontSize: "11px",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: "0.15em",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease-in-out",
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }
        a:hover { color: #D81F26 !important; }
        .privacy-contact-btn:hover { background: #414141 !important; color: #ffffff !important; transform: scale(1.02); }
        .privacy-contact-btn:active { transform: scale(0.95); }
        section:last-child { margin-bottom: 0 !important; }

        @media (max-width: 860px) {
          .privacy-layout { grid-template-columns: 1fr !important; }
          .privacy-aside { position: static !important; }
          .privacy-container { gap: 24px !important; }
        }

        @media (max-width: 640px) {
          .privacy-hero { padding: 50px 16px 40px !important; }
          .privacy-title { font-size: 32px !important; line-height: 1.2 !important; }
          .privacy-container { padding: 26px 16px 56px !important; }
          .privacy-container section {
            padding: 22px 20px !important;
            border-radius: 20px !important;
          }
          .privacy-container section h2 { font-size: 18px !important; }
          .privacy-container section p,
          .privacy-container section li { font-size: 14.5px !important; }
        }
      `}</style>

      <section style={styles.hero}>
        <div className="privacy-hero" style={styles.heroInner}>
          <p style={styles.eyebrow}>Privacy Policy</p>

          <h1 className="privacy-title" style={styles.h1}>
            CarFido Privacy Policy
          </h1>

          <p style={styles.intro}>
            This Privacy Policy explains how CarFido collects, uses, shares,
            and protects personal data.
          </p>

          <div style={styles.meta}>
            Effective Date: May 18, 2026
          </div>
        </div>
      </section>

      <div className="privacy-container privacy-layout" style={styles.container}>
        <main style={styles.main}>
          {sections.map((section) => (
            <section key={section.title} style={styles.section}>
              <h2 style={styles.h2}>{section.title}</h2>

              {section.body.length === 1 ? (
                <p style={styles.p}>{section.body[0]}</p>
              ) : (
                <ul style={styles.ul}>
                  {section.body.map((item) => (
                    <li key={item} style={styles.li}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </main>

        <aside className="privacy-aside" style={styles.aside}>
          <div style={styles.sideCard}>
            <h2 style={styles.sideTitle}>Privacy Requests</h2>

            <p style={styles.sideText}>
              To access, correct, delete, or request portability of your data,
              contact support@carfido.com.
            </p>

            <a href="mailto:support@carfido.com" className="privacy-contact-btn" style={styles.contactButton}>
              Email Support
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
